import React, { useMemo } from 'react'
import { connect, useSelector } from 'react-redux'

import { selectFullscreen } from '@store/actionSlices/appConfig'
import {
  BucketSource,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
  UnitGallery,
} from '@store/types'

import DataHandler from '@components/data-handler'
import { useIdleTimeHandler } from '@components/idle-time-handler'
import Skeleton from '@components/skeleton'

import PackageInfoCard from '@pages/properties-view/house-and-land/lot-view/package-info-card'

import {
  selectFromResult,
  useGetUnitGalleryQuery as useGetLotGalleryQuery,
} from '@api/unit-gallery'

import { LotProperty } from '@utilities/adgroup-utilities/types'

import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import { FileType } from '@adUtilities/constants/common'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'

export interface ComparableLotItemProps {
  comparableLot: LotProperty
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  appIdleControls: {
    active: boolean
    duration: number
  }
  effectType: string
  autoPlayIntervalInSeconds: number
  showInfo?: boolean
  onlyShowPropertyGalleries?: boolean
}

const NO_IMAGE_OBJECT: UnitGallery = {
  id: 'no-image',
  src: '',
  label: 'No Image',
  type: FileType.Image,
  noSpliceUrl: false,
}

function ComparableLotItem({
  comparableLot,
  projectIdentity,
  session,
  appIdleControls,
  effectType,
  autoPlayIntervalInSeconds,
  showInfo,
  onlyShowPropertyGalleries,
}: ComparableLotItemProps) {
  const fullscreen = useSelector(selectFullscreen)
  const lot = comparableLot.data
  const { projectId, projectName } = projectIdentity
  const isAppIdle = useIdleTimeHandler({
    isConnected: !!session?.connected,
    timeoutInSeconds: appIdleControls.duration,
    isIdleActive: appIdleControls.active,
  })

  const lotGalleryQuery = useGetLotGalleryQuery(
    { projectName, unitId: lot.name },
    { selectFromResult }
  )
  const { unitGalleryData: lotGalleryData } = lotGalleryQuery

  const transformImageAsset = (item: any): FloorImage => ({
    id: item.id,
    src: item.url,
    label: item.title || item.name,
    order: item.order,
  })

  const buildPropertyGalleryItems = (): PropertyGallery[] =>
    lotGalleryData.map((item: UnitGallery) => ({
      id: item.id,
      src: item.src,
      label: item.label || '',
      type: item.type,
      thumbnail: item.thumbnail,
      gradiant: item.type === FileType.Video ? 0.5 : 0,
      noSpliceUrl: false,
    }))

  const slides = useMemo(() => {
    const floorItems: FloorImage[] = lot.assets?.floorImage
      ? [transformImageAsset(lot.assets.floorImage)]
      : []

    const viewLineItems: FloorImage[] =
      lot.assets?.viewLinesImage?.map(transformImageAsset) || []

    const propertyGalleryItems = buildPropertyGalleryItems()

    const propertyGallery = mapPropertyGallery({
      projectId,
      floorImages: floorItems,
      viewLineImages: viewLineItems,
      propertyGalleries: propertyGalleryItems,
      onlyShowPropertyGallery: onlyShowPropertyGalleries || false,
    })

    const gallery = propertyGallery.length ? propertyGallery : [NO_IMAGE_OBJECT]

    return [gallery[0]].map((item) => ({
      id: item.id,
      label: item.label,
      src:
        item.type === FileType.Image && item.src
          ? item.src
          : item.thumbnail || '',
      thumbnail: item.thumbnail,
      originalSrc: item.src,
      bucketType: BucketSource.Legacy,
      fileType: item.type as FileType,
      noSpliceUrl: item.noSpliceUrl,
    }))
  }, [projectId, lot, lotGalleryData, onlyShowPropertyGalleries])

  return (
    <DataHandler
      payloads={{
        ...lotGalleryQuery,
        data: slides,
      }}
      skeletonFrame={<Skeleton containerClass="!w-full absolute" />}
    >
      <div className="relative flex h-full w-full bg-gray-700/50">
        <div
          className={`absolute bottom-0 z-10 h-auto w-full bg-shadow-horizontal transition duration-300 ease-in ${
            showInfo ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div
            className={`flex h-full transform items-stretch justify-between duration-200 ease-in ${
              fullscreen ? 'pb-2 pl-2' : 'pb-5 pl-5'
            } `}
          >
            <PackageInfoCard
              lot={lot}
              totalPrice={lot.price}
              connected={!!session?.connected}
              handlePackageInfoModal={() => {}}
              packageInfo={undefined}
              activePrecinctId={comparableLot.references.precinctId}
              activeStageId={comparableLot.references.stageId}
            />
          </div>
        </div>
        <div className="absolute inset-0">
          <CarouselProvider
            gallerySettings={{ effectType, autoPlayIntervalInSeconds }}
            slides={slides}
            activeSlideIndex={0}
            showControl={false}
            mousewheel={false}
            autoPlay={false}
            isAppIdle={isAppIdle}
          />
        </div>
      </div>
    </DataHandler>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    projectConfig: {
      propertyGalleryTab,
      gallerySettings: { type, intervalInSeconds },
      onlyShowPropertyGalleries,
    },
  }: RootStateTypeExtra) => ({
    projectIdentity,
    session,
    appIdleControls: propertyGalleryTab,
    effectType: type,
    autoPlayIntervalInSeconds: intervalInSeconds,
    onlyShowPropertyGalleries,
  })
)(ComparableLotItem)
